import { create } from 'zustand';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  message: string;
  variant: ToastVariant;
  duration?: number;
  title?: string;
}

export interface LoadingOverlay {
  id: string;
  message?: string;
}

interface UIState {
  sidebarCollapsed: boolean;
  mobileSidebarOpen: boolean;
  toastQueue: Toast[];
  loadingOverlays: LoadingOverlay[];
  activeModal: string | null;
}

interface UIActions {
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleMobileSidebar: () => void;
  setMobileSidebarOpen: (open: boolean) => void;
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  clearToasts: () => void;
  showLoading: (id: string, message?: string) => void;
  hideLoading: (id: string) => void;
  setActiveModal: (modalId: string | null) => void;
}

type UIStore = UIState & UIActions;

let toastIdCounter = 0;

export const useUIStore = create<UIStore>()((set, get) => ({
  sidebarCollapsed: false,
  mobileSidebarOpen: false,
  toastQueue: [],
  loadingOverlays: [],
  activeModal: null,

  toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

  setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),

  toggleMobileSidebar: () => set((state) => ({ mobileSidebarOpen: !state.mobileSidebarOpen })),

  setMobileSidebarOpen: (open) => set({ mobileSidebarOpen: open }),

  addToast: (toast) => {
    const id = String(++toastIdCounter);
    const newToast: Toast = { ...toast, id };
    set((state) => ({ toastQueue: [...state.toastQueue, newToast] }));

    // Auto-remove after duration (default 4s)
    const duration = toast.duration ?? 4000;
    if (duration > 0) {
      setTimeout(() => {
        get().removeToast(id);
      }, duration);
    }
  },

  removeToast: (id) =>
    set((state) => ({
      toastQueue: state.toastQueue.filter((t) => t.id !== id),
    })),

  clearToasts: () => set({ toastQueue: [] }),

  showLoading: (id, message) =>
    set((state) => ({
      loadingOverlays: [...state.loadingOverlays.filter((o) => o.id !== id), { id, message }],
    })),

  hideLoading: (id) =>
    set((state) => ({
      loadingOverlays: state.loadingOverlays.filter((o) => o.id !== id),
    })),

  setActiveModal: (modalId) => set({ activeModal: modalId }),
}));
